// Purpose: Let emailed sponsors and vendors review, correct and confirm existing event listings.
// Features: Private callable access, revision protection and optional normal account signup.
// Dependencies: Event/auth contexts, ReactQuill, shared signup controls and email registration.
// Behavior: Only public name/description/website are editable; credentials remain in memory.
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { httpsCallable } from "firebase/functions";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth, functions } from "../firebase";
import { useAuth } from "../auth_context";
import { useEventContext } from "../hooks/use_event_context";
import { completeEmailRegistration } from "../services/email_registration";
import EntryAccountSignup from "./entry_account_signup";
import FullPageSpinner from "./FullPageSpinner";
import SeoHead from "./seo_head";
import { sanitizeHtml } from "../utils/sanitize_html";
import { showStatus } from "../utils/status_helpers";

const COPY = {
  sponsor: { title: "Sponsorship", noun: "sponsorship", name: "Sponsor", get: "getSponsorVerification", save: "saveSponsorVerification",
    managed: "logo, contact email, sponsorship level, payment, award or location" },
  vendor: { title: "Vendor Details", noun: "vendor", name: "Vendor", get: "getVendorVerification", save: "saveVendorVerification",
    managed: "logo, contact email, category, booth, featured status, payment or location" },
};

export default function BusinessVerification({ family, getInvite }) {
  const copy = COPY[family];
  const idKey = `${family}Id`;
  const recordId = useParams()[idKey];
  const { eventId, eventslug, ready, eventLoading } = useEventContext({ redirectIfNotFound: true });
  const { user, authLoading } = useAuth();
  const token = getInvite(eventslug, recordId);
  const [review, setReview] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", url: "" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [status, setStatus] = useState(null);
  const [createAccount, setCreateAccount] = useState(false);
  const [password, setPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const registrationUserRef = useRef(null);
  const registrationCompleteRef = useRef(false);

  useEffect(() => {
    if (!ready || eventLoading || !eventId) return;
    let cancelled = false;
    setLoading(true);
    setReview(null);
    if (!token) { setLoading(false); return; }
    httpsCallable(functions, copy.get)({ eventId, [idKey]: recordId, token })
      .then(({ data }) => {
        if (cancelled) return;
        setReview(data);
        setForm({ name: data.name || "", description: sanitizeHtml(data.description || ""), url: data.url || "" });
      })
      .catch(() => { /* An unavailable link must not expose business details. */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eventId, eventslug, recordId, idKey, copy.get, ready, eventLoading, token, reloadKey]);

  const save = async (event) => {
    event.preventDefault();
    if (busy || !review) return;
    const registering = createAccount && (!user || registrationUserRef.current);
    if (registering && !registrationUserRef.current && (!firstName.trim() || !lastName.trim() || password.trim().length < 6 || !agreedToTerms)) {
      showStatus(setStatus, { type: "warning", message: "Enter your first and last name, use a password with at least 6 characters and accept the Terms & Conditions." });
      return;
    }
    setBusy(true);
    try {
      // Keep the new identity through setup/save retries; never create a second account.
      if (registering) {
        if (!registrationUserRef.current) {
          registrationUserRef.current = (await createUserWithEmailAndPassword(auth, review.email, password)).user;
          setPassword("");
        }
        if (!registrationCompleteRef.current) {
          await completeEmailRegistration(registrationUserRef.current, { firstName, lastName, agreedToTerms });
          registrationCompleteRef.current = true;
        }
      }
      const { data } = await httpsCallable(functions, copy.save)({ eventId, [idKey]: recordId, token, reviewRevision: review.reviewRevision, updates: form });
      setReview((previous) => ({ ...previous, ...data, confirmed: true }));
      showStatus(setStatus, { type: "success", message: `Your ${copy.noun} details are saved and confirmed. Thank you!` });
    } catch (error) {
      const messages = {
        "auth/email-already-in-use": "This email is already registered. Log in with your existing account, then return here.",
        "auth/weak-password": "Password must be at least 6 characters.",
        "auth/password-does-not-meet-requirements": "Choose a password that meets the account security requirements.",
        "auth/too-many-requests": "Too many attempts. Please try again later.",
        "functions/failed-precondition": `${family === "sponsor" ? "Sponsorship" : "Vendor"} details changed. Reload ${copy.title}${family === "sponsor" ? " Details" : ""}, review again, then save.`,
        "functions/invalid-argument": `Check the ${family} name, description and website. Use an http:// or https:// website address.`,
      };
      showStatus(setStatus, { type: "error", message: messages[error.code] || "Unable to finish saving. Please retry, or reopen your invitation email if the link has expired." });
    } finally { setBusy(false); }
  };

  if (!ready || eventLoading || loading || authLoading) return <FullPageSpinner message={`Loading your ${copy.noun}...`} />;
  return <>
    <SeoHead title={`Verify Your ${copy.title} | Let's Go Vote`} noindex={true} />
    <section id="top-content" className="container py-4" style={{ maxWidth: "800px" }}>
      <h1>Verify Your {copy.title}</h1>
      {status?.message && <div role="alert" className={`alert alert-${status.type === "success" ? "success" : status.type === "warning" ? "warning" : "danger"}`}>{status.message}</div>}
      {token && <button className="btn btn-outline-secondary mb-3" disabled={busy} onClick={() => setReloadKey((value) => value + 1)}>Reload {copy.title}{family === "sponsor" ? " Details" : ""}</button>}
      {!review ? <div role="alert" className="alert alert-danger">This review link is unavailable or expired. Reopen the original invitation email, or ask the event manager for a new invitation.</div> : <>
        <p>The event owner has added you as a {family} for <strong>{review.eventName}</strong>. Review and correct your public information below.</p>
        <div className="card shadow-sm"><div className="card-body">
          {review.logoUrl && <img src={review.logoUrl} alt={`${review.name} logo`} className="img-fluid mb-3" style={{ maxHeight: "160px" }} />}
          <dl className="row">
            {Object.entries({ "Contact Email": review.email,
              ...(family === "sponsor" ? { "Sponsorship Level": review.level || "Not specified",
                "Sponsor Award": review.sponsorAwardTitle || "Not specified", "Award Winner Registration": review.sponsorAwardWinnerReg || "Not specified" }
                : { Category: review.category || "Not specified", "Booth Number": review.boothNumber || "Not specified", "Featured Vendor": review.featuredVendor ? "Yes" : "No" }),
              [family === "sponsor" ? "Sponsorship Amount" : "Vendor Fee"]: `$${(Number(review.amountCents || 0) / 100).toFixed(2)}`,
              "Payment Status": review.paymentStatus || "Not recorded", "Approval Status": review.approvalStatus || "Not recorded",
              "Map Label": review.mapMarkerLabel || "Not specified", Location: review.mapMarkerTitle || "Not specified",
              "Location Notes": review.mapMarkerNote || "Not specified" }).map(([label, value]) => <React.Fragment key={label}>
              <dt className="col-sm-4">{label}</dt><dd className="col-sm-8 text-break">{value}</dd>
            </React.Fragment>)}
          </dl>
          <p className="form-text">Contact the event owner to change your {copy.managed}.</p>
          <form onSubmit={save}>
            <fieldset disabled={busy}>
              <label htmlFor="sponsor-review-name" className="form-label">{copy.name} Name</label>
              <input id="sponsor-review-name" className="form-control mb-3" required maxLength={300} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
              <label id="sponsor-review-description-label" htmlFor="sponsor-review-description" className="form-label">Description</label>
              <ReactQuill readOnly={busy} value={form.description} onChange={(description) => setForm((previous) => ({ ...previous, description }))}>
                <div id="sponsor-review-description" role="textbox" aria-multiline="true" aria-labelledby="sponsor-review-description-label" />
              </ReactQuill>
              <label htmlFor="sponsor-review-url" className="form-label mt-3">Website</label>
              <input id="sponsor-review-url" type="url" className="form-control" maxLength={2048} value={form.url} onChange={(event) => setForm({ ...form, url: event.target.value })} />
            </fieldset>
            {(!user || registrationUserRef.current) && <div className="row g-3 mt-1">
              <div className="col-sm-6"><label htmlFor="sponsor-first-name" className="form-label">First Name (for account)</label>
                <input id="sponsor-first-name" autoComplete="given-name" className="form-control" disabled={busy || Boolean(registrationUserRef.current)} value={firstName} onChange={(event) => setFirstName(event.target.value)} /></div>
              <div className="col-sm-6"><label htmlFor="sponsor-last-name" className="form-label">Last Name (for account)</label>
                <input id="sponsor-last-name" autoComplete="family-name" className="form-control" disabled={busy || Boolean(registrationUserRef.current)} value={lastName} onChange={(event) => setLastName(event.target.value)} /></div>
            </div>}
            <EntryAccountSignup user={user} accountCreated={Boolean(registrationUserRef.current)} createAccount={createAccount}
              onCreateAccountChange={(checked) => { setCreateAccount(checked); setPassword(""); }} password={password} onPasswordChange={setPassword}
              agreedToTerms={agreedToTerms} onTermsChange={setAgreedToTerms} disabled={busy} />
            {registrationCompleteRef.current && <div className="alert alert-info mt-3">Your account was created. Check your email to verify before voting. <a href="/dashboard?verifyRequired=1">Continue to your account</a></div>}
            {review.confirmed && <div className="alert alert-success mt-3">{copy.title}{family === "sponsor" ? " details" : ""} confirmed.</div>}
            <button className="btn btn-primary mt-3" type="submit" disabled={busy}>{busy ? "Saving..." : createAccount && (!user || registrationUserRef.current) && !registrationCompleteRef.current ? `Create Account and Confirm ${copy.title}` : `Save and Confirm ${copy.title}`}</button>
          </form>
        </div></div>
      </>}
    </section>
  </>;
}
